import type {
  AuthState,
  BootstrapSnapshot,
  BridgeError,
  BridgeCapabilities,
  BucketDocumentDraft,
  BucketListEntry,
  CaraDestination,
  FillSource,
  StateStamp,
  LearnedRule,
  LearningSettings,
  LearningSnapshot,
  RetryAdviceSettings,
  PageSessionSnapshot,
  SidebarDestination,
  SmartFillUiPreferences,
  SmartFillBucket,
  SmartFillBucketVisibility,
  SuperCopyGroupKind,
  SuperCopyEntity,
  SuperCopyReviewState,
  SuperCopySnapshot,
  TabContext,
  Workspace,
  WorkspaceUsers,
  WorkspaceConversion,
  BucketProfile
} from './dtos';

export const BRIDGE_CHANNEL = 'oyster.smartfill.bridge' as const;
export const BRIDGE_VERSION = 1 as const;

declare const bridgeRequestIdBrand: unique symbol;
export type BridgeRequestId = string & { readonly [bridgeRequestIdBrand]: true };

export type NoPayload = Record<string, never>;

export interface BucketListPayload {
  query?: string;
  limit?: number;
}

export interface BucketGetPayload {
  bucketId: string;
}

export interface BucketCreatePayload {
  operationId: string;
  name: string;
  visibility: SmartFillBucketVisibility;
  /** A complete entity-tree document. Node IDs must be omitted; the server mints them. */
  document: BucketDocumentDraft;
}

export interface BucketUpdatePayload {
  bucketId: string;
  expectedRevision: number;
  name?: string;
  visibility?: SmartFillBucketVisibility;
  /**
   * A complete replacement document. Node IDs are preserved for nodes that
   * keep their place and omitted for new nodes.
   */
  document?: BucketDocumentDraft;
}

export interface BucketDeletePayload {
  bucketId: string;
}

export interface BucketRevertLearningPayload {
  bucketId: string;
  expectedRevision: number;
}

export interface WorkspaceListPayload {
  query?: string;
  limit?: number;
  /** Omitted: current user. Null: all visible workspaces. */
  userId?: string | null;
}

export interface WorkspaceConvertPayload {
  workspaceId: string;
  operationId: string;
}

export type SettingsUpdatePayload = Partial<SmartFillUiPreferences>;

export type LearningSettingsUpdatePayload = Partial<LearningSettings>;

export type RetryAdviceSettingsUpdatePayload = Partial<
  Pick<RetryAdviceSettings, 'enabled' | 'siteEnabled'>
>;

/** Only references to server-owned proposals/receipts may cross the guest bridge. */
export interface LearningEntriesPayload {
  operationId: string;
  entryIds: string[];
}

export interface ActiveBucketSetPayload {
  bucketId: string | null;
}

export interface FillStartOncePayload {
  /**
   * Section paste: resolve against this entity's subtree (plus its
   * ancestors' own properties) instead of the whole bucket, so one repeated
   * record fills without row ambiguity. Absent preserves the tab's selection;
   * null explicitly returns to the whole client.
   */
  scopeEntityId?: string | null;
}

/**
 * Continuous paste may also be scoped: every page of the session then
 * resolves against one repeated record (one driver's subtree) so the wizard
 * never has to guess which sibling a step is about.
 */
export interface FillStartContinuousPayload {
  scopeEntityId?: string | null;
}

export interface FocusReviewFieldPayload {
  fieldId: string;
}

/**
 * An optional correction rule saved alongside a review edit. HINT teaches the
 * field's meaning by pointing at the bucket property the user picked; the
 * server derives a bucket-independent intent from it. SKIP says the field
 * should always be left blank on this form.
 */
export type ReviewTeachAction = { action: 'HINT'; propertyId: string } | { action: 'SKIP' };

export interface SaveReviewEditPayload {
  fieldId: string;
  value: string;
  teach?: ReviewTeachAction;
}

export interface SuperCopySaveValue {
  id: string;
  semanticKey: string;
  label: string;
  value: string;
  reviewState: SuperCopyReviewState;
  accepted: boolean;
}

export interface SuperCopySaveGroup {
  id: string;
  kind: SuperCopyGroupKind;
  label: string;
  entity?: SuperCopyEntity | null;
  values: SuperCopySaveValue[];
}

export interface SuperCopySaveClient {
  id: string;
  name: string;
  groups: SuperCopySaveGroup[];
}

export interface SuperCopySavePayload {
  draftId: string;
  visibility: SmartFillBucketVisibility;
  clients: SuperCopySaveClient[];
}

export interface OpenCaraPayload {
  destination: CaraDestination;
}

export interface BucketDeleteResult {
  deleted: true;
}

export interface RuleClearPayload {
  ruleId: string;
}

export interface RuleClearResult {
  cleared: true;
}

export interface FocusReviewFieldResult {
  focused: true;
}

export interface OpenCaraResult {
  opened: true;
}

export interface RequestDefinition<Payload, Result> {
  payload: Payload;
  result: Result;
}

export interface BridgeRequestMap {
  bootstrap: RequestDefinition<NoPayload, BootstrapSnapshot>;
  'capabilities.get': RequestDefinition<NoPayload, BridgeCapabilities>;
  'buckets.list': RequestDefinition<BucketListPayload, BucketListEntry[]>;
  'buckets.get': RequestDefinition<BucketGetPayload, SmartFillBucket>;
  'buckets.create': RequestDefinition<BucketCreatePayload, SmartFillBucket>;
  'buckets.update': RequestDefinition<BucketUpdatePayload, SmartFillBucket>;
  'buckets.delete': RequestDefinition<BucketDeletePayload, BucketDeleteResult>;
  'buckets.revertLearning': RequestDefinition<BucketRevertLearningPayload, SmartFillBucket>;
  'workspaces.list': RequestDefinition<WorkspaceListPayload, Workspace[]>;
  'workspaces.users': RequestDefinition<NoPayload, WorkspaceUsers>;
  'workspaces.buckets': RequestDefinition<{ workspaceId: string }, BucketListEntry[]>;
  'workspaces.convert': RequestDefinition<WorkspaceConvertPayload, WorkspaceConversion>;
  /** Builds one profile-shaped bucket from the workspace's filled ACORD forms. */
  'workspaces.convertAcord': RequestDefinition<WorkspaceConvertPayload, WorkspaceConversion>;
  'profiles.list': RequestDefinition<NoPayload, BucketProfile[]>;
  'settings.get': RequestDefinition<NoPayload, SmartFillUiPreferences>;
  'settings.update': RequestDefinition<SettingsUpdatePayload, SmartFillUiPreferences>;
  'learning.settings.get': RequestDefinition<NoPayload, LearningSettings>;
  'learning.settings.update': RequestDefinition<LearningSettingsUpdatePayload, LearningSettings>;
  'learning.get': RequestDefinition<NoPayload, LearningSnapshot>;
  'learning.stop': RequestDefinition<NoPayload, LearningSnapshot>;
  'learning.finish': RequestDefinition<NoPayload, LearningSnapshot>;
  'learning.apply': RequestDefinition<LearningEntriesPayload, LearningSnapshot>;
  'learning.undo': RequestDefinition<LearningEntriesPayload, LearningSnapshot>;
  'retryAdvice.settings.get': RequestDefinition<NoPayload, RetryAdviceSettings>;
  'retryAdvice.settings.update': RequestDefinition<
    RetryAdviceSettingsUpdatePayload,
    RetryAdviceSettings
  >;
  'activeBucket.set': RequestDefinition<ActiveBucketSetPayload, FillSource | null>;
  'fill.startOnce': RequestDefinition<FillStartOncePayload, PageSessionSnapshot>;
  'fill.startContinuous': RequestDefinition<FillStartContinuousPayload, PageSessionSnapshot>;
  'fill.continueContinuous': RequestDefinition<NoPayload, PageSessionSnapshot>;
  'fill.finishContinuous': RequestDefinition<NoPayload, PageSessionSnapshot>;
  'review.focusField': RequestDefinition<FocusReviewFieldPayload, FocusReviewFieldResult>;
  'review.saveEdit': RequestDefinition<SaveReviewEditPayload, PageSessionSnapshot>;
  'rules.list': RequestDefinition<NoPayload, LearnedRule[]>;
  'rules.clear': RequestDefinition<RuleClearPayload, RuleClearResult>;
  'superCopy.start': RequestDefinition<NoPayload, SuperCopySnapshot>;
  'superCopy.cancel': RequestDefinition<NoPayload, SuperCopySnapshot>;
  'superCopy.save': RequestDefinition<SuperCopySavePayload, SuperCopySnapshot>;
  'cara.open': RequestDefinition<OpenCaraPayload, OpenCaraResult>;
}

export type BridgeCommand = keyof BridgeRequestMap;
export type RequestPayload<C extends BridgeCommand> = BridgeRequestMap[C]['payload'];
export type RequestResult<C extends BridgeCommand> = BridgeRequestMap[C]['result'];

export const BRIDGE_COMMANDS = [
  'bootstrap',
  'capabilities.get',
  'buckets.list',
  'buckets.get',
  'buckets.create',
  'buckets.update',
  'buckets.delete',
  'buckets.revertLearning',
  'workspaces.list',
  'workspaces.users',
  'workspaces.buckets',
  'workspaces.convert',
  'workspaces.convertAcord',
  'profiles.list',
  'settings.get',
  'settings.update',
  'learning.settings.get',
  'learning.settings.update',
  'learning.get',
  'learning.stop',
  'learning.finish',
  'learning.apply',
  'learning.undo',
  'retryAdvice.settings.get',
  'retryAdvice.settings.update',
  'activeBucket.set',
  'fill.startOnce',
  'fill.startContinuous',
  'fill.continueContinuous',
  'fill.finishContinuous',
  'review.focusField',
  'review.saveEdit',
  'rules.list',
  'rules.clear',
  'superCopy.start',
  'superCopy.cancel',
  'superCopy.save',
  'cara.open'
] as const satisfies readonly BridgeCommand[];

export interface BridgeEventMap {
  'bootstrap.updated': BootstrapSnapshot;
  'auth.updated': AuthState;
  'tab.updated': TabContext;
  'activeBucket.updated': FillSource | null;
  'pageSession.updated': PageSessionSnapshot;
  'sidebar.navigate': SidebarDestination;
  'superCopy.updated': SuperCopySnapshot;
  'learning.changed': LearningSnapshot;
  'rules.changed': NoPayload;
}

export type BridgeEvent = keyof BridgeEventMap;
export type EventPayload<E extends BridgeEvent> = BridgeEventMap[E];

export const BRIDGE_EVENTS = [
  'bootstrap.updated',
  'auth.updated',
  'tab.updated',
  'activeBucket.updated',
  'pageSession.updated',
  'sidebar.navigate',
  'superCopy.updated',
  'learning.changed',
  'rules.changed'
] as const satisfies readonly BridgeEvent[];

interface EnvelopeBase {
  channel: typeof BRIDGE_CHANNEL;
  version: typeof BRIDGE_VERSION;
}

export interface GuestRequestFor<C extends BridgeCommand> extends EnvelopeBase {
  direction: 'guest-to-host';
  kind: 'request';
  requestId: BridgeRequestId;
  command: C;
  payload: RequestPayload<C>;
}

export type GuestRequest<C extends BridgeCommand = BridgeCommand> = {
  [K in C]: GuestRequestFor<K>;
}[C];

export interface HostSuccessResponseFor<C extends BridgeCommand> extends EnvelopeBase {
  direction: 'host-to-guest';
  kind: 'response';
  requestId: BridgeRequestId;
  command: C;
  ok: true;
  result: RequestResult<C>;
}

export interface HostErrorResponseFor<C extends BridgeCommand> extends EnvelopeBase {
  direction: 'host-to-guest';
  kind: 'response';
  requestId: BridgeRequestId;
  command: C;
  ok: false;
  error: BridgeError;
}

export type HostResponse<C extends BridgeCommand = BridgeCommand> = {
  [K in C]: HostSuccessResponseFor<K> | HostErrorResponseFor<K>;
}[C];

export interface HostEventFor<E extends BridgeEvent> extends EnvelopeBase {
  direction: 'host-to-guest';
  kind: 'event';
  event: E;
  payload: EventPayload<E>;
  stamp?: StateStamp;
}

export type HostEvent<E extends BridgeEvent = BridgeEvent> = {
  [K in E]: HostEventFor<K>;
}[E];

export interface HostHandshakePayload {
  hostInstanceId: string;
}

export interface HostHandshake extends EnvelopeBase {
  direction: 'host-to-guest';
  kind: 'handshake';
  payload: HostHandshakePayload;
}

export type HostMessage = HostResponse | HostEvent | HostHandshake;

const BRIDGE_IDENTIFIER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

export function isBridgeIdentifier(value: unknown): value is string {
  return typeof value === 'string' && BRIDGE_IDENTIFIER_PATTERN.test(value);
}

export function assertBridgeIdentifier(value: unknown, label = 'identifier'): string {
  if (!isBridgeIdentifier(value)) {
    throw new TypeError(
      `${label} must be 1-128 characters using letters, numbers, ".", "_", ":", or "-".`
    );
  }
  return value;
}

export function asBridgeRequestId(value: string): BridgeRequestId {
  return assertBridgeIdentifier(value, 'requestId') as BridgeRequestId;
}

export function createBridgeRequestId(): BridgeRequestId {
  if (typeof globalThis.crypto?.randomUUID !== 'function') {
    throw new Error('crypto.randomUUID is required to create a Smart Fill bridge request ID.');
  }
  return asBridgeRequestId(globalThis.crypto.randomUUID());
}

export function isBridgeCommand(value: unknown): value is BridgeCommand {
  return typeof value === 'string' && (BRIDGE_COMMANDS as readonly string[]).includes(value);
}

export function isBridgeEvent(value: unknown): value is BridgeEvent {
  return typeof value === 'string' && (BRIDGE_EVENTS as readonly string[]).includes(value);
}

export function createGuestRequest<C extends BridgeCommand>(
  command: C,
  payload: RequestPayload<NoInfer<C>>,
  requestId: string = createBridgeRequestId()
): GuestRequest<C> {
  return {
    channel: BRIDGE_CHANNEL,
    version: BRIDGE_VERSION,
    direction: 'guest-to-host',
    kind: 'request',
    requestId: asBridgeRequestId(requestId),
    command,
    payload
  } as GuestRequest<C>;
}

export function createHostSuccessResponse<C extends BridgeCommand>(
  request: GuestRequest<C>,
  result: RequestResult<NoInfer<C>>
): HostSuccessResponseFor<C> {
  return {
    channel: BRIDGE_CHANNEL,
    version: BRIDGE_VERSION,
    direction: 'host-to-guest',
    kind: 'response',
    requestId: request.requestId,
    command: request.command,
    ok: true,
    result
  };
}

export function createHostEvent<E extends BridgeEvent>(
  event: E,
  payload: EventPayload<NoInfer<E>>,
  stamp?: StateStamp
): HostEventFor<E> {
  return {
    channel: BRIDGE_CHANNEL,
    version: BRIDGE_VERSION,
    direction: 'host-to-guest',
    kind: 'event',
    event,
    payload,
    ...(stamp ? { stamp } : {})
  };
}

export function createHostHandshake(hostInstanceId: string): HostHandshake {
  return {
    channel: BRIDGE_CHANNEL,
    version: BRIDGE_VERSION,
    direction: 'host-to-guest',
    kind: 'handshake',
    payload: {
      hostInstanceId: assertBridgeIdentifier(hostInstanceId, 'hostInstanceId')
    }
  };
}
